import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast, Toaster } from "sonner";

/* Numero que recebe os briefings. Trocar aqui muda o formulario inteiro. */
const WHATSAPP_NUMBER = "5511942681399";

/* O briefing vai direto pro WhatsApp como mensagem pronta — sem backend,
   sem banco e sem dado pessoal de cliente parado em servidor. */
function buildWhatsAppUrl(f) {
  const linhas = [
    "Olá, Acervo 3D! Quero encomendar uma peça.",
    "",
    `Nome: ${f.nome}`,
    `E-mail: ${f.email}`,
    `WhatsApp: ${f.whatsapp}`,
    `Tipo de peça: ${f.tipo_peca}`,
    f.prazo ? `Prazo: ${f.prazo}` : null,
    f.referencias ? `Referências: ${f.referencias}` : null,
    "",
    `História: ${f.mensagem}`,
  ].filter(Boolean);
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    linhas.join("\n")
  )}`;
}

const TIPOS = [
  "Presente personalizado",
  "Homenagem / memória",
  "Decoração",
  "Lembrança de evento",
  "Outro (conto na mensagem)",
];

const PRAZOS = ["Sem pressa", "Até 15 dias", "Até 30 dias", "Urgente (< 7 dias)"];

const initial = {
  nome: "",
  email: "",
  whatsapp: "",
  tipo_peca: "",
  prazo: "",
  referencias: "",
  mensagem: "",
};

export default function BriefingForm() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [step, setStep] = useState(1);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const pick = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const canGo1 = form.nome.trim().length > 1 && form.tipo_peca;
  const canGo2 = form.email.includes("@") && form.whatsapp.trim().length > 5;
  const canSubmit = canGo1 && canGo2 && form.mensagem.trim().length > 3;

  const submit = () => {
    if (!canSubmit) return;
    setLoading(true);
    // Aberto dentro do clique do usuario para nao cair no bloqueio de popup.
    window.open(buildWhatsAppUrl(form), "_blank", "noopener,noreferrer");
    setSent(true);
    setLoading(false);
    toast.success("Briefing pronto no WhatsApp — é só enviar a mensagem.");
  };

  if (sent) {
    return (
      <>
        <Toaster theme="dark" position="bottom-right" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="p-8 md:p-12 border border-[var(--line-2)] bg-[var(--bg-2)]"
          data-testid="briefing-sent"
        >
          <div className="font-mono-tech text-[10px] text-[var(--accent)] mb-6">
            Recebido · Obrigada
          </div>
          <h3 className="font-serif-display text-4xl md:text-5xl leading-none tracking-tight mb-6">
            Sua história chegou até nós.
          </h3>
          <p className="font-sans text-[15px] leading-[1.75] text-[var(--ink-2)] mb-6 max-w-[42ch]">
            Abrimos o WhatsApp com seu briefing já preenchido — é só tocar em
            enviar que a conversa começa. Retornamos em até 48h com
            referências, prazo e orçamento inicial. 🤍
          </p>
          <a
            href={buildWhatsAppUrl(form)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block font-mono-tech text-[11px] text-[var(--accent)] link-under mb-10"
            data-testid="briefing-whatsapp-fallback"
          >
            A janela não abriu? Abrir o WhatsApp →
          </a>
          <button
            onClick={() => {
              setForm(initial);
              setSent(false);
              setStep(1);
            }}
            className="font-mono-tech text-[11px] text-[var(--ink)] link-under"
            data-testid="briefing-new"
          >
            Enviar outra história →
          </button>
        </motion.div>
      </>
    );
  }

  return (
    <>
      <Toaster theme="dark" position="bottom-right" />
      <div className="p-8 md:p-12 border border-[var(--line-2)] bg-[var(--bg-2)]">
        <div className="flex items-center justify-between mb-8">
          <div className="font-mono-tech text-[10px] text-[var(--ink-3)]">
            Briefing · passo {step} de 3
          </div>
          <div className="flex gap-1.5">
            {[1, 2, 3].map((n) => (
              <span
                key={n}
                className={`w-6 h-px transition-all duration-500 ${
                  step >= n ? "bg-[var(--accent)]" : "bg-[var(--line)]"
                }`}
              />
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div
              key="s1"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4 }}
            >
              <h3 className="font-serif-display text-3xl md:text-4xl leading-none tracking-tight mb-8">
                Como podemos te chamar?
              </h3>

              <Field label="Seu nome">
                <input
                  value={form.nome}
                  onChange={set("nome")}
                  placeholder="Ex: Maria"
                  className="w-full bg-transparent border-b border-[var(--line-2)] focus:border-[var(--accent)] outline-none py-3 font-serif-display text-2xl tracking-tight text-[var(--ink)] placeholder:text-[var(--ink-3)] transition-colors duration-500"
                  data-testid="briefing-nome"
                />
              </Field>

              <Field label="Que tipo de peça você tem em mente?">
                <div className="flex flex-wrap gap-2">
                  {TIPOS.map((t) => (
                    <button
                      key={t}
                      onClick={() => pick("tipo_peca", t)}
                      className={`font-mono-tech text-[10px] px-4 py-2 border transition-colors duration-300 ${
                        form.tipo_peca === t
                          ? "border-[var(--accent)] text-[var(--accent)]"
                          : "border-[var(--line-2)] text-[var(--ink-2)] hover:text-[var(--ink)]"
                      }`}
                      data-testid={`briefing-tipo-${t.split(" ")[0].toLowerCase()}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </Field>

              <NextBar disabled={!canGo1} onClick={() => setStep(2)} testid="briefing-step-2" />
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="s2"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4 }}
            >
              <h3 className="font-serif-display text-3xl md:text-4xl leading-none tracking-tight mb-8">
                Onde te encontramos?
              </h3>

              <Field label="Seu melhor email">
                <input
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="voce@email.com"
                  className="w-full bg-transparent border-b border-[var(--line-2)] focus:border-[var(--accent)] outline-none py-3 font-serif-display text-2xl tracking-tight text-[var(--ink)] placeholder:text-[var(--ink-3)] transition-colors duration-500"
                  data-testid="briefing-email"
                />
              </Field>

              <Field label="WhatsApp (com DDD)">
                <input
                  value={form.whatsapp}
                  onChange={set("whatsapp")}
                  placeholder="(11) 99999-9999"
                  className="w-full bg-transparent border-b border-[var(--line-2)] focus:border-[var(--accent)] outline-none py-3 font-serif-display text-2xl tracking-tight text-[var(--ink)] placeholder:text-[var(--ink-3)] transition-colors duration-500"
                  data-testid="briefing-whatsapp"
                />
              </Field>

              <Field label="Prazo desejado (opcional)">
                <div className="flex flex-wrap gap-2">
                  {PRAZOS.map((p) => (
                    <button
                      key={p}
                      onClick={() => pick("prazo", p)}
                      className={`font-mono-tech text-[10px] px-4 py-2 border transition-colors duration-300 ${
                        form.prazo === p
                          ? "border-[var(--accent)] text-[var(--accent)]"
                          : "border-[var(--line-2)] text-[var(--ink-2)] hover:text-[var(--ink)]"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </Field>

              <div className="flex items-center justify-between mt-10 pt-6 border-t border-[var(--line)]">
                <button
                  onClick={() => setStep(1)}
                  className="font-mono-tech text-[10px] text-[var(--ink-2)] link-under"
                >
                  ← Voltar
                </button>
                <NextBar disabled={!canGo2} inline onClick={() => setStep(3)} testid="briefing-step-3" />
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              key="s3"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4 }}
            >
              <h3 className="font-serif-display text-3xl md:text-4xl leading-none tracking-tight mb-8">
                Nos conta a história.
              </h3>

              <Field label="Referências / links (opcional)">
                <input
                  value={form.referencias}
                  onChange={set("referencias")}
                  placeholder="Cole links do Insta, Pinterest, ou descreva"
                  className="w-full bg-transparent border-b border-[var(--line-2)] focus:border-[var(--accent)] outline-none py-3 font-sans text-base tracking-tight text-[var(--ink)] placeholder:text-[var(--ink-3)] transition-colors duration-500"
                  data-testid="briefing-referencias"
                />
              </Field>

              <Field label="Sua mensagem — o que essa peça significa?">
                <textarea
                  value={form.mensagem}
                  onChange={set("mensagem")}
                  rows={5}
                  placeholder="Fala pra gente a intenção, o momento, a pessoa..."
                  className="w-full bg-transparent border border-[var(--line-2)] focus:border-[var(--accent)] outline-none p-4 font-sans text-[15px] leading-[1.7] text-[var(--ink)] placeholder:text-[var(--ink-3)] transition-colors duration-500 resize-none"
                  data-testid="briefing-mensagem"
                />
              </Field>

              <div className="flex items-center justify-between mt-10 pt-6 border-t border-[var(--line)]">
                <button
                  onClick={() => setStep(2)}
                  className="font-mono-tech text-[10px] text-[var(--ink-2)] link-under"
                >
                  ← Voltar
                </button>
                <button
                  onClick={submit}
                  disabled={!canSubmit || loading}
                  className="group inline-flex items-center gap-3 bg-[var(--accent)] hover:bg-[var(--accent-hover)] disabled:opacity-40 disabled:cursor-not-allowed text-[#0b0b0b] font-mono-tech text-[11px] px-6 py-4 transition-colors duration-300"
                  data-testid="briefing-submit"
                >
                  <span>{loading ? "Enviando..." : "Enviar minha história"}</span>
                  <span className="inline-block w-6 h-px bg-current group-hover:w-10 transition-[width] duration-500" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-10 pt-6 border-t border-[var(--line)] font-mono-tech text-[10px] text-[var(--ink-3)] flex items-center justify-between">
          <span>Resposta em até 48h</span>
          <span>Seus dados ficam com a gente</span>
        </div>
      </div>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-8">
      <div className="font-mono-tech text-[10px] text-[var(--ink-3)] mb-3">
        {label}
      </div>
      {children}
    </div>
  );
}

function NextBar({ disabled, onClick, inline, testid }) {
  const btn = (
    <button
      onClick={onClick}
      disabled={disabled}
      className="group inline-flex items-center gap-3 font-mono-tech text-[11px] text-[var(--ink)] disabled:opacity-30 disabled:cursor-not-allowed"
      data-testid={testid}
    >
      <span>Continuar</span>
      <span className="inline-block w-8 h-px bg-current group-hover:w-14 transition-[width] duration-500" />
    </button>
  );
  if (inline) return btn;
  return (
    <div className="mt-10 pt-6 border-t border-[var(--line)] flex justify-end">
      {btn}
    </div>
  );
}
